import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Text } from '../../base/Text';
import { FilterChips, type FilterPeriod } from '../../base/FilterChips';
import { GroupSelector, type GroupSelectorGroup } from '../GroupSelector';
import styles from './FilterBottomSheet.module.css';

interface FilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  filterPeriod: FilterPeriod;
  selectedGroupId: string | null;
  groups: GroupSelectorGroup[];
  onFilterChange: (period: FilterPeriod) => void;
  onGroupChange: (groupId: string | null) => void;
}

export const FilterBottomSheet = ({
  isOpen,
  onClose,
  filterPeriod,
  selectedGroupId,
  groups,
  onFilterChange,
  onGroupChange,
}: FilterBottomSheetProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <div className={styles.sheet} role="dialog" aria-modal aria-label={t('filter.title', 'Filter')}>
        <div className={styles.handle} />
        <div className={styles.header}>
          <Text variant="title">{t('filter.title', 'Filter')}</Text>
          <button type="button" onClick={onClose} className={styles.closeBtn} aria-label="Close">
            <X size={24} />
          </button>
        </div>
        <div className={styles.content}>
          <section className={styles.section}>
            <Text variant="meta" muted className={styles.sectionTitle}>
              {t('filter.byDate', 'By date')}
            </Text>
            <FilterChips selected={filterPeriod} onChange={onFilterChange} />
          </section>
          {groups.length > 0 && (
            <section className={styles.section}>
              <Text variant="meta" muted className={styles.sectionTitle}>
                {t('filter.byGroup', 'By group')}
              </Text>
              <GroupSelector
                groups={groups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={onGroupChange}
              />
            </section>
          )}
        </div>
      </div>
    </>
  );
};
